// The custom lobby: a page of the PLAY section (ui-map-interface.md
// §16.17, R3087, R3088).
//
// A Premium player hosts a private custom world with HOST on the EVENT
// tile. The lobby is a row of the meta server, so this page mirrors no
// rule of a lobby: it draws the view the server last answered, and every
// press is one call whose answer is the next view. The page polls the view
// every `CUSTOM_LOBBY.pollSec` seconds while it stands and the browser
// shows the page; the poll names the revision it holds, and the server
// answers 304 while it stands.
//
// The address `/?lobby=<code>` opens the hub on this page. A reader who is
// no member joins the lobby when the page opens. While the page stands the
// address carries the code, so a reload opens the page again.
//
// The clock of the head counts each second, and it is written OUT of the
// diffed markup, as the hosting window of the arcade writes its clock: a
// rewrite a second would close a select box under the host's finger.
//
// The code rows, the person rows and the two-press gate come from the
// lobby kit (`ui/lobbykit.ts`), which the arcade hosting window shares.

import { CLAN_NAME_MAX_LEN, CODE_LOBBY_NOT_FOUND, CUSTOM_LOBBY, MODE_CUSTOM } from '../../../gen/balance';
import { CHROME_ARM_MS } from '../../../gen/presentation';
import { hubCustom as S, hubPanel as P } from '../../../gen/strings';
import { ApiError, messageOf } from '../../../net/api';
import {
  createLobby, getLobby, joinLobby, leaveLobby, lobbyAct, putLobbyMember, putLobbySettings,
  type LobbyMember, type LobbyView, type MemberPatch, type SettingsPatch,
} from '../../../net/customlobby';
import { FACTIONS, factionColor, factionName, hex } from '../../../world';
import { isoNear, pad2 } from '../../../locale/stamp';
import { ArmGate } from '../../arm';
import { button, pillGroup, pillMulti, selectBox, tag } from '../../choice/choice';
import { LOBBY_PARAM, hubLobbyHref } from '../../hublink';
import {
  codeRowsHtml, handCode, personRowHtml, type CodeRowsLook, type ShareSink,
} from '../../lobbykit';
import { formatSpan } from '../hubstate';
import {
  backBar, card, ctlRow, esc, factionTag, rows, srow, switchButton, textBox,
  type PanelContext,
} from '../panel';
import { codeKind, normalizeHostCode } from '../../../arcade/hostcode';
import type { MapSpec } from './lobbymap';

/** The section word of a lobby (`SECTION_PARAM`): `lobby` and the code in
 *  lower case. A sign-in with a provider carries it through the state
 *  cookie, whose words are lower-case letters and digits (R1659). */
const SECTION_HEAD = 'lobby';

/** The section word that names one lobby. */
export function lobbySection(code: string): string {
  return `${SECTION_HEAD}${code.toLowerCase()}`;
}

/** The code a section word names, or '' for a word that names no lobby. */
export function lobbyOfSection(word: string): string {
  if (!word.startsWith(SECTION_HEAD)) return '';
  const code = normalizeHostCode(word.slice(SECTION_HEAD.length));
  return codeKind(code) === 'custom' ? code : '';
}

/** The code of `?lobby=` on an address, or '' for none or a bad one. */
export function readLobbyParam(params: URLSearchParams): string {
  const code = normalizeHostCode(params.get(LOBBY_PARAM) ?? '');
  return codeKind(code) === 'custom' ? code : '';
}

/** Writes `?lobby=` into the address, or takes it off for ''. A lobby is
 *  no history entry, so this is a `replaceState`. */
function writeLobbyParam(code: string): void {
  const url = new URL(location.href);
  if (code === '') url.searchParams.delete(LOBBY_PARAM);
  else url.searchParams.set(LOBBY_PARAM, code);
  if (url.href !== location.href) history.replaceState(history.state, '', url.toString());
}

/** The routes the page calls; a test passes fakes. */
export interface LobbyApi {
  getLobby: typeof getLobby;
  joinLobby: typeof joinLobby;
  leaveLobby: typeof leaveLobby;
  putLobbySettings: typeof putLobbySettings;
  putLobbyMember: typeof putLobbyMember;
  lobbyAct: typeof lobbyAct;
  createLobby: typeof createLobby;
}

const REAL_API: LobbyApi = {
  getLobby, joinLobby, leaveLobby, putLobbySettings, putLobbyMember, lobbyAct, createLobby,
};

/** What the page reads outside itself. */
export interface LobbyDeps {
  api?: Partial<LobbyApi>;
  share?: ShareSink;
  /** The map preview. The default loads `lobbymap.ts` with `import()`. */
  drawMap?: (m: MapSpec, widthPx: number) => Promise<HTMLCanvasElement>;
  /** True while the browser shows the page; a hidden page does not poll. */
  visible?: () => boolean;
  /** Writes the code into the address; a test passes a stub. */
  writeAddress?: (code: string) => void;
}

/** The width of the map preview canvas, in chrome px. The canvas scales
 *  to its box by CSS; this is the drawing width. */
const MAP_DRAW_PX = 320;

async function loadMap(m: MapSpec, widthPx: number): Promise<HTMLCanvasElement> {
  const mod = await import('./lobbymap');
  return mod.drawLobbyMap(m, widthPx, window.devicePixelRatio || 1);
}

/** The setting keys a number box writes, with the range each one keeps. */
type NumKey = 'population' | 'timescale' | 'tacticalScale' | 'combatPace' | 'seed'
  | 'seasonDays' | 'truceSec' | 'stage1Frac' | 'stage2Frac';

/** The ranges of `CUSTOM_LOBBY`, by setting key. */
const RANGES: Readonly<Record<Exclude<NumKey, 'population' | 'seed'>, { min: number; max: number }>> = {
  timescale: CUSTOM_LOBBY.timescale,
  tacticalScale: CUSTOM_LOBBY.tacticalScale,
  combatPace: CUSTOM_LOBBY.combatPace,
  seasonDays: CUSTOM_LOBBY.seasonDays,
  truceSec: CUSTOM_LOBBY.truceSec,
  stage1Frac: CUSTOM_LOBBY.stage1Frac,
  stage2Frac: CUSTOM_LOBBY.stage2Frac,
};

/** The seconds of one hour: the truce box reads hours. */
const HOUR_SEC = 3600;

/** A whole-number key: the box steps by one. */
const WHOLE: ReadonlySet<NumKey> = new Set(['population', 'timescale', 'tacticalScale', 'combatPace', 'seed', 'seasonDays']);

/** A length of wall seconds as hours and minutes: `8:05`. */
export function hoursMinutes(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / HOUR_SEC)}:${pad2(Math.floor((s % HOUR_SEC) / 60))}`;
}

/** The value a number box holds for one key, in the unit it shows. */
function shown(key: NumKey, v: number): number {
  return key === 'truceSec' ? v / HOUR_SEC : v;
}

/** The lowest seed a host may set. A new lobby draws one of 1 and up, and
 *  a host may set any whole number up to `Number.MAX_SAFE_INTEGER`, the
 *  largest the JSON of the view carries exactly. */
const SEED_MIN = 1;

/** The setting a number box writes, from the text it holds, clamped to
 *  its range; null for a text that is no number. */
export function settingOf(key: NumKey, text: string, seats: number): number | null {
  const n = Number(text);
  if (text.trim() === '' || !Number.isFinite(n)) return null;
  let v = key === 'truceSec' ? n * HOUR_SEC : n;
  if (WHOLE.has(key) || key === 'truceSec') v = Math.round(v);
  if (key === 'population') return Math.min(Math.max(v, CUSTOM_LOBBY.minPopulation), seats);
  if (key === 'seed') return Math.min(Math.max(SEED_MIN, v), Number.MAX_SAFE_INTEGER);
  const r = RANGES[key];
  return Math.min(Math.max(v, r.min), r.max);
}

/** One line of the page. A line in mixed case is a sentence, such as a
 *  refusal of the server, so it takes the `mc` class and the Body type, as
 *  `panel.ts` `empty` writes it; a line in capitals stays a caption
 *  (DESIGN.md "The Sentence Rule", R1739). */
export function lineHtml(cls: string, text: string, attrs = ''): string {
  return `<p class="${cls}${/\p{Ll}/u.test(text) ? ' mc' : ''}"${attrs}>${esc(text)}</p>`;
}

/** Why a final lobby is gone (R3110). An ended lobby names the final
 *  state of its run in one line, with the budget phrase when the hosting
 *  budget came back; a cancelled lobby whose budget came back states the
 *  phrase on a line of its own. */
export function goneLines(v: LobbyView): string {
  const back = v.refunded === true ? S.budget_back : '';
  if (v.state === 'ended') {
    const why = v.endReason === 'failed' ? S.gone_failed
      : v.endReason === 'cancelled' ? S.gone_run_cancelled : S.gone_released;
    return lineHtml('cl-why', back !== '' ? `${why} · ${back}` : why);
  }
  const why = v.state === 'cancelled' ? S.gone_cancelled : S.gone_dissolved;
  return lineHtml('cl-why', why) + (back !== '' ? lineHtml('cl-why', back) : '');
}

/** The words of each state of a lobby. A started lobby reads STARTING,
 *  the word of the run state `starting` on the ops console, until its
 *  world opens; `stateTag` then writes LIVE (R3168). */
const STATE_WORD: Readonly<Record<LobbyView['state'], string>> = {
  draft: S.state_draft, open: S.state_open, started: S.state_starting, ended: S.state_ended,
  cancelled: S.state_cancelled, dissolved: S.state_dissolved,
};

/** The state tag of the head: a started lobby is STARTING in the accent
 *  until its world opens, then LIVE in the Good ink (R3168). */
function stateTag(v: LobbyView): string {
  if (v.state === 'started' && v.worldOpen === true) return tag(S.state_live, 'good');
  return tag(STATE_WORD[v.state], v.state === 'open' || v.state === 'started' ? 'accent' : 'plain');
}

/** The instants of a started run, as the lobby view and the host state
 *  name them. */
export interface RunClock {
  opensAt?: number;
  closesAt?: number;
  worldOpen?: boolean;
}

/**
 * The clock line of a started lobby (§16.17, R3168): THE WORLD OPENS IN
 * {left} before the open, THE WORLD IS OPENING after the open time while
 * the server does not yet let a join pass, and THE WORLD CLOSES IN {left}
 * once it does. The lobby page and the custom seat card of PLAY (§16.6)
 * share it.
 */
export function runClockLine(r: RunClock, now: number): string {
  if (r.worldOpen === true) {
    const close = r.closesAt ?? 0;
    return close > now ? S.world_closes({ left: formatSpan(close - now) }) : S.world_open;
  }
  const open = r.opensAt ?? 0;
  return open > now ? S.world_opens({ left: formatSpan(open - now) }) : S.world_opening;
}

/** True when a join of the world of a started run passes now: the server
 *  named the world and opened it (R3168). */
export function runJoinable(worldId: number | undefined, worldOpen: boolean | undefined): boolean {
  return (worldId ?? 0) > 0 && worldOpen === true;
}

/** The faction ids in roster order. */
const FACTION_IDS: readonly number[] = FACTIONS.map((f) => f.id);

/**
 * The custom lobby page. The PLAY panel holds one and hands it the acts
 * whose word starts with `cl-`.
 */
export class CustomLobby {
  /** The code of the lobby the page stands on, or '' when it stands on none. */
  code = '';
  private view: LobbyView | null = null;
  /** The sentence of the last refusal, or ''. It stands until the next act. */
  private notice = '';
  /** The line COPY and SHARE wrote, or ''. */
  private note = '';
  /** A failure of the first read: the page shows it in place of the view. */
  private failure = '';
  private busy = false;
  /** The ADVANCED fold, kept across rewrites. */
  private advOpen = false;
  private shown = false;
  private poll: ReturnType<typeof setTimeout> | undefined;
  private tick: ReturnType<typeof setInterval> | undefined;
  private root: HTMLElement | null = null;
  private mapKey = '';
  private mapImg: HTMLCanvasElement | null = null;
  private readonly gate: ArmGate;
  private readonly api: LobbyApi;

  constructor(
    private readonly ctx: PanelContext,
    /** The page leaves for the tiles: BACK, LEAVE, or a lobby that ended. */
    private readonly onExit: () => void,
    private readonly deps: LobbyDeps = {},
  ) {
    this.gate = new ArmGate(CHROME_ARM_MS, () => ctx.refresh());
    this.api = { ...REAL_API, ...deps.api };
  }

  /** True while the page stands in place of the tiles. */
  get active(): boolean {
    return this.code !== '' && this.shown;
  }

  /** The view the page holds, for a test. */
  get current(): LobbyView | null {
    return this.view;
  }

  /** Opens the page on one lobby: it reads the view and joins it where the
   *  reader is no member. */
  open(code: string): void {
    if (code !== this.code) {
      this.code = code;
      this.view = null;
      this.failure = '';
      this.notice = '';
      this.note = '';
      this.gate.clear(false);
      void this.first();
    }
    this.show();
  }

  /** HOST: a new lobby, and the page on it. */
  host(): void {
    if (this.busy) return;
    this.busy = true;
    this.ctx.refresh();
    this.api.createLobby().then((v) => {
      this.busy = false;
      this.code = v.code;
      this.take(v);
      this.show();
    }, (err: unknown) => {
      this.busy = false;
      this.notice = messageOf(err);
      this.ctx.refresh();
    });
  }

  /** The refusal of a HOST press, for the tile to show; '' for none. */
  get hostNotice(): string {
    return this.code === '' ? this.notice : '';
  }

  /** The section was shown again: the poll and the clock run. */
  resume(): void {
    if (this.code !== '') this.show();
  }

  /** The section was hidden: the poll and the clock stop. The lobby
   *  stays, and the page opens on it again. */
  pause(): void {
    this.stopTimers();
  }

  /** The page leaves for the tiles. The reader stays a member. */
  private exit(): void {
    this.shown = false;
    this.stopTimers();
    (this.deps.writeAddress ?? writeLobbyParam)('');
    this.onExit();
  }

  /** The page forgets the lobby: LEAVE, or a lobby that ended. */
  private forget(): void {
    this.code = '';
    this.view = null;
    this.exit();
  }

  private show(): void {
    this.shown = true;
    (this.deps.writeAddress ?? writeLobbyParam)(this.code);
    this.schedule();
    if (this.tick === undefined) this.tick = setInterval(() => this.paintClock(), 1000);
    this.ctx.refresh();
  }

  private stopTimers(): void {
    if (this.poll !== undefined) clearTimeout(this.poll);
    if (this.tick !== undefined) clearInterval(this.tick);
    this.poll = undefined;
    this.tick = undefined;
  }

  /** True while the poll runs: a hidden page and an ended lobby poll not. */
  get polling(): boolean {
    return this.poll !== undefined;
  }

  private schedule(): void {
    if (this.poll !== undefined) clearTimeout(this.poll);
    this.poll = undefined;
    if (!this.shown || this.ended()) return;
    this.poll = setTimeout(() => { void this.pollOnce(); }, CUSTOM_LOBBY.pollSec * 1000);
  }

  /** True for a final lobby: ended, cancelled or dissolved. */
  private ended(): boolean {
    const st = this.view?.state;
    return st === 'ended' || st === 'cancelled' || st === 'dissolved';
  }

  private visible(): boolean {
    if (this.deps.visible) return this.deps.visible();
    return typeof document === 'undefined' || document.visibilityState !== 'hidden';
  }

  /** The first read, and the join of a reader who is no member. */
  private async first(): Promise<void> {
    const code = this.code;
    try {
      let v = await this.api.getLobby(code);
      if (code !== this.code) return;
      if (v !== null && !v.members.some((m) => m.id === v!.you)
        && (v.state === 'draft' || v.state === 'open')) {
        v = await this.api.joinLobby(code);
      }
      if (code !== this.code || v === null) return;
      this.take(v);
    } catch (err) {
      if (code !== this.code) return;
      // A reader who is no member may read nothing of the lobby: the join
      // is the way in, and its answer is the view or the reason.
      if (err instanceof ApiError && err.status === 403) {
        try {
          const v = await this.api.joinLobby(code);
          if (code === this.code) this.take(v);
          return;
        } catch (e) {
          err = e;
        }
      }
      if (code !== this.code) return;
      this.failure = messageOf(err);
      if (err instanceof ApiError && err.code === CODE_LOBBY_NOT_FOUND) this.stopTimers();
      this.ctx.refresh();
    }
  }

  private async pollOnce(): Promise<void> {
    this.poll = undefined;
    const code = this.code;
    if (!this.shown || code === '') return;
    if (this.visible()) {
      try {
        const v = await this.api.getLobby(code, this.view?.rev);
        if (code === this.code && v !== null) this.take(v);
      } catch {
        // A failed poll keeps the picture; the next one asks again.
      }
    }
    if (code === this.code) this.schedule();
  }

  /** Takes a new view. A change of the members makes the arming stale:
   *  the row an armed KICK named may now hold somebody else. */
  private take(v: LobbyView): void {
    const before = this.view;
    if (before !== null && before.members.map((m) => m.id).join() !== v.members.map((m) => m.id).join()) {
      this.gate.clear(false);
    }
    this.view = v;
    this.failure = '';
    if (this.ended()) this.stopTimers();
    this.ctx.refresh();
  }

  /** One act that answers the next view. */
  private call(run: () => Promise<LobbyView>): void {
    if (this.busy) return;
    this.busy = true;
    this.notice = '';
    this.ctx.refresh();
    run().then((v) => {
      this.busy = false;
      this.take(v);
    }, (err: unknown) => {
      this.busy = false;
      this.notice = messageOf(err);
      this.ctx.refresh();
    });
  }

  private patch(p: SettingsPatch): void {
    const code = this.code;
    this.call(() => this.api.putLobbySettings(code, p));
  }

  private member(id: string, p: MemberPatch): void {
    const code = this.code;
    this.call(() => this.api.putLobbyMember(code, id, p));
  }

  // ---- the presses ------------------------------------------------------

  /** One press of the page. Answers false for a word it does not know. */
  act(action: string, el: HTMLElement): boolean {
    const v = this.view;
    const id = el.dataset.id ?? '';
    switch (action) {
      case 'cl-back': this.ctx.play('sfx-menu-press'); this.exit(); return true;
      case 'cl-copycode': handCode(this.code, 'code', (l) => this.say(l), this.deps.share); return true;
      case 'cl-copylink':
      case 'cl-sharelink': handCode(this.linkOf(), 'link', (l) => this.say(l), this.deps.share); return true;
      default: break;
    }
    if (v === null) return action.startsWith('cl-');
    const s = v.settings;
    switch (action) {
      case 'cl-seats': {
        const n = Number(id);
        if (v.isHost && Number.isFinite(n) && n !== s.seats) this.patch({ seats: n });
        return true;
      }
      case 'cl-fill': if (v.isHost) this.patch({ fill: !s.fill }); return true;
      case 'cl-fac': {
        const f = Number(id);
        if (!v.isHost || !FACTION_IDS.includes(f)) return true;
        const on = s.factions.includes(f);
        if (on && s.factions.length <= CUSTOM_LOBBY.minFactions) return true;
        if (on && v.members.some((m) => m.faction === f)) return true;
        const next = on ? s.factions.filter((x) => x !== f) : [...s.factions, f].sort((a, b) => a - b);
        this.patch({ factions: next });
        return true;
      }
      case 'cl-flag': {
        if (!v.isHost || !v.envFlags.includes(id) || v.locked.includes('flags')) return true;
        const on = s.flags.includes(id);
        this.patch({ flags: on ? s.flags.filter((x) => x !== id) : [...s.flags, id] });
        return true;
      }
      case 'cl-mfac':
      case 'cl-msq': {
        // The option id is "<public id>:<value>"; a public id is a UUID,
        // so the value follows the last colon.
        const at = id.lastIndexOf(':');
        const who = id.slice(0, at);
        const val = Number(id.slice(at + 1));
        if (!v.isHost || at <= 0 || !Number.isFinite(val) || !v.members.some((m) => m.id === who)) return true;
        this.member(who, action === 'cl-mfac' ? { faction: val } : { squad: val });
        return true;
      }
      case 'cl-kick': {
        const who = el.dataset.member ?? '';
        if (!v.isHost || !v.members.some((m) => m.id === who)) return true;
        if (!this.armed(`cl-kick:${who}`)) return true;
        this.member(who, { kick: true });
        return true;
      }
      case 'cl-open':
        if (v.isHost && v.state === 'draft' && v.canOpen.ok) this.call(() => this.api.lobbyAct(v.code, 'open'));
        return true;
      case 'cl-cancel':
        if (v.isHost && v.state === 'open' && this.armed('cl-cancel')) {
          this.call(() => this.api.lobbyAct(v.code, 'cancel'));
        }
        return true;
      case 'cl-start':
        if (v.isHost && v.state === 'open' && v.canStart.ok && this.armed('cl-start')) {
          this.call(() => this.api.lobbyAct(v.code, 'start'));
        }
        return true;
      case 'cl-leave': this.leave(v); return true;
      case 'cl-joinworld':
        // Nobody enters before the open (R3168): the server refuses the
        // ticket, and a world before its open stands frozen.
        if (v.state === 'started' && runJoinable(v.worldId, v.worldOpen)) {
          this.ctx.join({ mode: MODE_CUSTOM, world: v.worldId });
        }
        return true;
      default:
        return action.startsWith('cl-');
    }
  }

  /** The two-press gate (R837): true on the second press of `key`. */
  private armed(key: string): boolean {
    if (this.gate.press(key)) return true;
    this.ctx.play('sfx-menu-press');
    return false;
  }

  /** LEAVE. The host of a draft closes it, so that press takes two; any
   *  other member leaves with one. */
  private leave(v: LobbyView): void {
    if (v.isHost && v.state !== 'draft') return;
    if (v.isHost && !this.armed('cl-leave')) return;
    if (this.busy) return;
    this.busy = true;
    this.api.leaveLobby(v.code).then(() => {
      this.busy = false;
      this.forget();
    }, (err: unknown) => {
      this.busy = false;
      this.notice = messageOf(err);
      this.ctx.refresh();
    });
  }

  private say(line: string): void {
    this.note = line;
    this.ctx.refresh();
  }

  /** The address of the lobby: the server's own, or this page's. */
  private linkOf(): string {
    const l = this.view?.link ?? '';
    if (l !== '') return l;
    return this.code === '' ? '' : `${location.origin}${hubLobbyHref(this.code)}`;
  }

  // ---- the boxes --------------------------------------------------------

  /** Wires the boxes of the page after a rewrite, and paints the map and
   *  the clock into it. */
  wire(root: HTMLElement): void {
    this.root = root;
    const v = this.view;
    for (const box of root.querySelectorAll<HTMLInputElement>('input[data-cl]')) {
      if (box.dataset.clWired === '1') continue;
      box.dataset.clWired = '1';
      box.addEventListener('change', () => this.boxChanged(box));
    }
    const adv = root.querySelector<HTMLDetailsElement>('details.cl-adv');
    if (adv && adv.dataset.clWired !== '1') {
      adv.dataset.clWired = '1';
      adv.addEventListener('toggle', () => { this.advOpen = adv.open; });
    }
    if (v !== null) this.paintMap(root, v);
    this.paintClock();
  }

  private boxChanged(box: HTMLInputElement): void {
    const v = this.view;
    if (v === null || !v.isHost) return;
    const key = box.dataset.cl ?? '';
    if (key === 'name') {
      if (box.value !== v.settings.name) this.patch({ name: box.value.trim() });
      return;
    }
    const k = key as NumKey;
    const val = settingOf(k, box.value, v.settings.seats);
    if (val === null || val === v.settings[k]) return;
    this.patch({ [k]: val });
  }

  /** The map preview: drawn once per seed, seats and factions, and put
   *  into the canvas of each rewrite. */
  private paintMap(root: HTMLElement, v: LobbyView): void {
    const s = v.settings;
    const key = `${s.seed}|${s.seats}|${s.factions.join(',')}|${s.population}`;
    if (key !== this.mapKey) {
      this.mapKey = key;
      this.mapImg = null;
      const spec: MapSpec = { seed: s.seed, seats: s.seats, population: s.population, factions: s.factions };
      (this.deps.drawMap ?? loadMap)(spec, MAP_DRAW_PX).then((img) => {
        if (key !== this.mapKey) return;
        this.mapImg = img;
        if (this.root) this.blit(this.root);
      }, () => { /* the page stands without its map */ });
    }
    this.blit(root);
  }

  private blit(root: HTMLElement): void {
    const img = this.mapImg;
    const cv = root.querySelector<HTMLCanvasElement>('canvas.cl-map');
    if (!img || !cv || cv.dataset.key === this.mapKey) return;
    cv.width = img.width;
    cv.height = img.height;
    cv.getContext('2d')?.drawImage(img, 0, 0);
    cv.dataset.key = this.mapKey;
  }

  /** The clock line of the head, from the server's instant. */
  clockLine(): string {
    const v = this.view;
    if (v === null) return '';
    const now = this.ctx.now();
    const left = (at: number | undefined): string => formatSpan((at ?? now) - now);
    switch (v.state) {
      case 'draft': return v.expiresAt === undefined ? '' : S.draft_ends({ left: left(v.expiresAt) });
      case 'open': return v.autostartAt === undefined ? '' : S.autostart({ left: left(v.autostartAt) });
      case 'started': return runClockLine(v, now);
      default: return '';
    }
  }

  private paintClock(): void {
    const el = this.root?.querySelector('.cl-clock');
    if (!el) return;
    const line = this.clockLine();
    if (el.textContent !== line) el.textContent = line;
  }

  // ---- the markup -------------------------------------------------------

  /** The whole page, from the view and nothing else. */
  html(): string {
    const back = backBar(S.back, 'cl-back', S.title);
    const v = this.view;
    if (v === null) {
      const line = this.failure !== '' ? lineHtml('cl-why bad', this.failure) : lineHtml('cl-why', S.loading);
      return `${back}<div class="cl-page">${line}</div>`;
    }
    if (this.ended()) {
      return `${back}<div class="cl-page">${this.head(v)}${goneLines(v)}</div>`;
    }
    return `${back}<div class="cl-page">`
      + this.head(v)
      + (this.notice !== '' ? lineHtml('cl-why bad', this.notice, ' role="alert"') : '')
      + (this.note !== '' ? lineHtml('cl-why', this.note) : '')
      + '<div class="cl-cols">'
      + `<div class="cl-col">${this.membersCard(v)}${this.mapCard()}</div>`
      + `<div class="cl-col">${this.settingsCard(v)}</div>`
      + '</div>'
      + this.foot(v)
      + '</div>';
  }

  private head(v: LobbyView): string {
    const name = v.settings.name !== '' ? v.settings.name : v.host.name;
    const look: CodeRowsLook = {
      press: (act, label) => button(label, { act: `cl-${act}` }, { size: 'compact' }),
      words: { code: S.code, link: S.link, copy: S.copy, share: S.share },
      kick: 'k',
      rows: { code: 'row cl-coderow', link: 'row cl-linkrow' },
    };
    return '<div class="cl-head">'
      + `<h3 class="cl-name">${esc(name)}</h3>`
      + stateTag(v)
      + '<span class="cl-clock" aria-live="off"></span>'
      + '</div>'
      + `<div class="cl-codes">${codeRowsHtml(v.code, this.linkOf(), look)}</div>`;
  }

  /** The members, one group a faction that is on, and the members whose
   *  faction is AUTO or off under ANY FACTION. */
  private membersCard(v: LobbyView): string {
    const on = v.settings.factions;
    const groups = on.map((f) => {
      const ms = v.members.filter((m) => m.faction === f);
      const total = v.commanders[String(f)] ?? ms.length;
      const count = S.faction_count({
        total: String(total), humans: S.humans({ n: ms.length }), bots: S.bots({ n: Math.max(0, total - ms.length) }),
      });
      return `<div class="cl-fac"><div class="cl-fhead">${factionTag(f)}<span class="k">${esc(count)}</span></div>`
        + (ms.length === 0 ? lineHtml('cl-none', S.nobody) : ms.map((m) => this.memberRow(v, m)).join(''))
        + '</div>';
    }).join('');
    const loose = v.members.filter((m) => !on.includes(m.faction));
    const any = loose.length === 0 ? ''
      : `<div class="cl-fac"><div class="cl-fhead"><span class="k">${esc(S.any_faction)}</span></div>`
        + loose.map((m) => this.memberRow(v, m)).join('') + '</div>';
    return card(S.members, '▸', String(v.members.length), groups + any, 'cl-members');
  }

  private memberRow(v: LobbyView, m: LobbyMember): string {
    const name = `<span class="cl-pname">${esc(m.name)}</span>`
      // HOST is a role mark, not a prize: the plain tag ink (R1798).
      + (m.host ? tag(S.host_mark) : '')
      + (m.id === v.you ? tag(S.you, 'accent') : '');
    let acts = '';
    if (v.isHost) {
      const facs = [{ id: `${m.id}:0`, label: S.auto },
        ...v.settings.factions.map((f) => ({ id: `${m.id}:${f}`, label: factionName(f), ink: hex(factionColor(f)) }))];
      acts += selectBox(facs, `${m.id}:${m.faction}`, 'cl-mfac',
        { label: S.faction_pick({ name: m.name }), uid: `cl-mfac-${m.id}`, size: 'compact' });
      if (CUSTOM_LOBBY.squads > 1) {
        const sq = [{ id: `${m.id}:0`, label: S.auto },
          ...Array.from({ length: CUSTOM_LOBBY.squads }, (_, i) => ({ id: `${m.id}:${i + 1}`, label: S.squad_n({ n: i + 1 }) }))];
        acts += selectBox(sq, `${m.id}:${m.squad}`, 'cl-msq',
          { label: S.squad_pick({ name: m.name }), uid: `cl-msq-${m.id}`, size: 'compact' });
      }
      if (!m.host) {
        const key = `cl-kick:${m.id}`;
        acts += button(S.kick, { act: 'cl-kick', data: { member: m.id } },
          { variant: 'danger', size: 'compact', armFace: S.kick_armed, armed: this.gate.is(key) });
      }
    } else if (m.faction > 0 || m.squad > 0) {
      acts = m.squad > 0 && CUSTOM_LOBBY.squads > 1 ? `<span class="k">${esc(S.squad_n({ n: m.squad }))}</span>` : '';
    }
    return personRowHtml(`cl-person${m.id === v.you ? ' you' : ''}`, `<span class="cl-pn">${name}</span>`, acts, 'cl-acts');
  }

  /** The settings: the host changes them, every member reads them. */
  private settingsCard(v: LobbyView): string {
    const s = v.settings;
    const host = v.isHost;
    const lockedWhy = (key: string): string => (v.locked.includes(key) ? S.locked : '');
    const facList = FACTIONS.map((f) => ({ id: String(f.id), label: factionName(f.id), ink: hex(f.banner) }));
    const picked = s.factions.map(String);

    let body: string;
    if (host) {
      // A row like the number rows: the label at the left, the box at
      // the right edge, its text set to the right.
      const name = srow(S.name, '',
        textBox('cl-name', `id="cl-name" class="cl-text" type="text" data-cl="name" autocomplete="off"`
          + ` maxlength="${CLAN_NAME_MAX_LEN}" aria-label="${esc(S.name)}"`, v.host.name, s.name));
      const seats = srow(S.seats, '', pillGroup(CUSTOM_LOBBY.seats.map((n) => ({ id: String(n), label: String(n) })),
        String(s.seats), 'cl-seats', { label: S.seats, size: 'compact' }));
      const fill = srow(S.fill, esc(s.fill ? S.fill_desc : S.fill_off_desc), switchButton(s.fill, ' data-act="cl-fill"', S.fill));
      const pop = s.fill ? this.numRow(v, 'population', S.population, CUSTOM_LOBBY.minPopulation, s.seats) : '';
      const facs = '<div class="cl-facs">'
        + `<span class="k">${esc(S.factions)}</span>`
        + pillMulti(facList.map((c) => {
          // A faction that is on stays on at the floor of the count, and
          // while a member plays in it (`roster_rule`).
          if (!picked.includes(c.id)) return c;
          if (picked.length <= CUSTOM_LOBBY.minFactions) {
            return { ...c, locked: true, title: S.factions_min({ n: CUSTOM_LOBBY.minFactions }) };
          }
          if (v.members.some((m) => String(m.faction) === c.id)) return { ...c, locked: true, title: S.faction_held };
          return c;
        }), picked, 'cl-fac', { label: S.factions, size: 'compact', wrap: true })
        + `${lineHtml('cl-note', S.factions_min({ n: CUSTOM_LOBBY.minFactions }))}</div>`;
      body = name + seats + fill + pop + facs;
    } else {
      body = rows([
        [S.name, esc(s.name !== '' ? s.name : v.host.name)],
        [S.seats, String(s.seats)],
        [S.fill, s.fill ? P.switch_on : P.switch_off],
        ...(s.fill ? [[S.population, String(s.population)] as [string, string]] : []),
      ]) + `<div class="cl-facs"><span class="k">${esc(S.factions)}</span>`
        + s.factions.map((f) => factionTag(f)).join('') + '</div>';
    }
    const adv = `<details class="cl-adv"${this.advOpen ? ' open' : ''}><summary>${esc(S.advanced)}</summary>`
      + this.numRow(v, 'timescale', S.timescale)
      + this.numRow(v, 'tacticalScale', S.tactical_scale)
      + this.numRow(v, 'combatPace', S.combat_pace)
      + this.numRow(v, 'seed', S.seed, SEED_MIN, Number.MAX_SAFE_INTEGER)
      + this.numRow(v, 'seasonDays', S.season_days)
      + this.numRow(v, 'truceSec', S.truce_hours)
      + this.numRow(v, 'stage1Frac', S.stage1)
      + this.numRow(v, 'stage2Frac', S.stage2)
      + this.flagsBlock(v, lockedWhy('flags'))
      + '</details>';
    const wall = `<p class="cl-wall">${esc(S.wall({ length: hoursMinutes(v.wallSec) }))}`
      + (v.nextRun === undefined ? ''
        : ` · ${esc(S.next_run({ label: v.nextRun.label, at: isoNear(v.nextRun.opensAt, this.ctx.now()) }))}`)
      + '</p>';
    return card(S.settings, '▸', '', body + adv + wall, 'cl-settings');
  }

  /** One number row: a box for the host, the figure for everybody else.
   *  A key in `locked` stands greyed with the reason (R3090). */
  private numRow(v: LobbyView, key: NumKey, label: string, min?: number, max?: number): string {
    const value = shown(key, v.settings[key]);
    const locked = v.locked.includes(key);
    const r = key === 'population' || key === 'seed' ? null : RANGES[key];
    const lo = min ?? (r === null ? 0 : shown(key, r.min));
    const hi = max ?? (r === null ? undefined : shown(key, r.max));
    const why = locked ? `<span class="cl-lock">${esc(S.locked)}</span>` : '';
    if (!v.isHost) return srow(label, why, `<span class="cl-fig">${esc(String(value))}</span>`, locked ? 'locked' : '');
    const step = WHOLE.has(key) ? '1' : 'any';
    const box = `<input type="number" class="cl-num" data-cl="${key}" inputmode="decimal" min="${lo}"`
      + `${hi === undefined ? '' : ` max="${hi}"`} step="${step}" value="${value}"`
      + ` aria-label="${esc(label)}"${locked ? ' disabled' : ''}>`;
    return srow(label, why, box, locked ? 'locked' : '');
  }

  /** The flags of the environment, each a switch the host may turn off
   *  (R3093). An environment with no flag shows no block. */
  private flagsBlock(v: LobbyView, lockedWhy: string): string {
    if (v.envFlags.length === 0) return '';
    const list = v.envFlags.map((f) => {
      const on = v.settings.flags.includes(f);
      const ctl = v.isHost && lockedWhy === ''
        ? switchButton(on, ` data-act="cl-flag" data-id="${esc(f)}"`, f)
        : `<span class="cl-fig">${on ? P.switch_on : P.switch_off}</span>`;
      return srow(f.toUpperCase(), '', ctl);
    }).join('');
    return `<div class="cl-flags"><span class="k">${esc(S.flags)}</span>${lineHtml('cl-note', S.flags_desc)}${list}</div>`;
  }

  /** The map preview card. */
  private mapCard(): string {
    return card(S.map, '▸', '', `<canvas class="cl-map" aria-hidden="true"></canvas>${lineHtml('cl-note', S.map_note)}`, 'cl-mapcard');
  }

  /** The acts of the foot, each greyed with the server's reason. */
  private foot(v: LobbyView): string {
    const acts: string[] = [];
    let why = '';
    if (v.state === 'started') {
      acts.push(button(S.join_world, { act: 'cl-joinworld' },
        { variant: 'primary', disabled: !runJoinable(v.worldId, v.worldOpen) }));
    } else if (v.isHost && v.state === 'draft') {
      acts.push(button(S.leave, { act: 'cl-leave' },
        { variant: 'danger', armFace: S.leave_armed, armed: this.gate.is('cl-leave'), disabled: this.busy }));
      acts.push(button(S.open, { act: 'cl-open' }, { variant: 'primary', disabled: this.busy || !v.canOpen.ok }));
      if (!v.canOpen.ok) why = v.canOpen.message ?? '';
    } else if (v.isHost && v.state === 'open') {
      acts.push(button(S.cancel, { act: 'cl-cancel' },
        { variant: 'danger', armFace: S.cancel_armed, armed: this.gate.is('cl-cancel'), disabled: this.busy }));
      acts.push(button(S.start, { act: 'cl-start' },
        { variant: 'primary', armFace: S.start_armed, armed: this.gate.is('cl-start'), disabled: this.busy || !v.canStart.ok }));
      if (!v.canStart.ok) why = v.canStart.message ?? '';
    } else {
      acts.push(button(S.leave, { act: 'cl-leave' }, { disabled: this.busy }));
    }
    return '<div class="cl-foot">'
      + (why !== '' ? lineHtml('cl-why', `✕ ${why}`) : '')
      + ctlRow(acts.join('')) + '</div>';
  }
}
